// src/pages/EditSobeScript.jsx
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { getAuthHeaders } from "../services/authHeaders";

// classes padrão (iguais às do SobeScript.jsx)
const fieldClass =
  "w-full h-11 rounded-lg px-3 " +
  "bg-white dark:bg-slate-800 " +
  "border border-slate-300 dark:border-slate-700 " +
  "text-slate-900 dark:text-slate-100 " +
  "placeholder-slate-500 dark:placeholder-slate-400 " +
  "outline-none transition " +
  "focus:border-blue-500 focus:ring-4 focus:ring-blue-400/20";

const cardClass =
  "rounded-2xl p-6 space-y-6 " +
  "bg-[rgb(var(--card))] border border-[rgb(var(--border))] " +
  "shadow-[0_1px_0_rgba(0,0,0,0.03),0_10px_20px_-10px_rgba(0,0,0,0.08)] dark:shadow-sm";

function EditSobeScript() {
  const { uuid } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    platform: "",
    project_name: "",
    conversion_name: "",
    max_clicks: "",
    redirect: "no",
    redirect_url: "",
    affiliate_link: "",
    producer_link: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const XANO_BASE_URL = "https://api.sobevenda.com.br/api-proxy/script";

  useEffect(() => {
    async function fetchScript() {
      try {
        const { data } = await axios.get(`${XANO_BASE_URL}/script/${uuid}`, {
          headers: getAuthHeaders(),
        });
        setFormData({
          platform: data.platform || "",
          project_name: data.project_name || "",
          conversion_name: data.conversion_name || "",
          max_clicks: data.max_clicks ?? "",
          redirect: data.redirect || "no",
          redirect_url: data.redirect_url || "",
          affiliate_link: data.affiliate_link || "",
          producer_link: data.producer_link || "",
        });
      } catch (err) {
        console.error(err);
        toast.error("Erro ao carregar o SobeScript.");
      } finally {
        setLoading(false);
      }
    }
    fetchScript();
  }, [uuid]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${XANO_BASE_URL}/update-sobescript`,
        { ...formData, uuid },
        { headers: getAuthHeaders() }
      );
      toast.success("SobeScript atualizado com sucesso!");
      navigate("/sobescripts");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar alterações.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const ok = window.confirm("Tem certeza que deseja excluir este SobeScript?");
    if (!ok) return;
    try {
      await axios.delete(`${XANO_BASE_URL}/delete-sobescript`, {
        headers: getAuthHeaders(),
        data: { uuid },
      });
      toast.success("SobeScript excluído com sucesso!");
      navigate("/sobescripts");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao excluir o SobeScript.");
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-3xl ml-6">
        <div className={cardClass}>Carregando…</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl ml-6 text-[rgb(var(--fg))]">
      <h1 className="text-3xl font-bold mb-6">Editar SobeScript</h1>

      <div className={cardClass}>
        {/* Plataforma (somente leitura) */}
        <div>
          <label className="font-bold block mb-1">Plataforma *</label>
          <input
            type="text"
            name="platform"
            value={formData.platform}
            disabled
            className={`${fieldClass} bg-slate-100 dark:bg-slate-900 cursor-not-allowed`}
          />
        </div>

        {/* Projeto */}
        <div>
          <label className="font-bold block mb-1">Nome do Projeto *</label>
          <input
            type="text"
            name="project_name"
            value={formData.project_name}
            onChange={handleChange}
            className={fieldClass}
            placeholder="Ex: Weight Loss"
          />
        </div>

        {/* Conversão */}
        <div>
          <label className="font-bold block mb-1">
            Nome da Ação de Conversão *
          </label>
          <input
            type="text"
            name="conversion_name"
            value={formData.conversion_name}
            onChange={handleChange}
            className={fieldClass}
            placeholder="Ex: pixel weight loss"
          />
        </div>

        {/* Links (opcionais) */}
        <div>
          <label className="font-bold block mb-1">
            Link de Afiliado <span className="font-normal text-slate-500">(opcional)</span>
          </label>
          <input
            type="text"
            name="affiliate_link"
            value={formData.affiliate_link}
            onChange={handleChange}
            className={fieldClass}
            placeholder="Ex: https://seulink.com/afiliado"
          />
        </div>

        <div>
          <label className="font-bold block mb-1">
            Link do Produtor <span className="font-normal text-slate-500">(opcional)</span>
          </label>
          <input
            type="text"
            name="producer_link"
            value={formData.producer_link}
            onChange={handleChange}
            className={fieldClass}
            placeholder="Ex: https://seulink.com/produtor"
          />
        </div>

        {/* Redirecionamento */}
        <div className="flex items-center gap-4">
          <label className="font-bold">Redirecionar após captura?</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="redirect"
                value="yes"
                checked={formData.redirect === "yes"}
                onChange={handleChange}
              />
              <span>Sim</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="redirect"
                value="no"
                checked={formData.redirect === "no"}
                onChange={handleChange}
              />
              <span>Não</span>
            </label>
          </div>
        </div>

        {formData.redirect === "yes" && (
          <div className="space-y-4">
            <div>
              <label className="font-bold block mb-1">
                URL de Redirecionamento{" "}
                <span className="font-normal text-slate-500">
                  (para onde o usuário será enviado após o clique)
                </span>
              </label>
              <input
                type="text"
                name="redirect_url"
                value={formData.redirect_url}
                onChange={handleChange}
                className={fieldClass}
                placeholder="Ex: https://seusite.com/checkout"
              />
              <div className="text-xs text-slate-500 mt-1">
                Informe a URL para onde o visitante deve ser enviado após
                capturar o clique. Se usar página de obrigado ou checkout,
                coloque o link aqui.
              </div>
            </div>

            <div>
              <label className="font-bold block mb-1">
                Qtd Máx. de Cliques{" "}
                <span className="font-normal text-slate-500">(opcional)</span>
              </label>
              <input
                type="number"
                name="max_clicks"
                min="0"
                value={formData.max_clicks}
                onChange={handleChange}
                className={fieldClass}
                placeholder="0 para ilimitado"
              />
              <div className="text-xs text-slate-500 mt-1">
                Deixe em 0 para ilimitado. Se definir um valor, após esse
                número de cliques o redirecionamento será bloqueado.
              </div>
            </div>
          </div>
        )}

        {/* Ações */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-3 px-6 rounded"
          >
            {saving ? "Salvando..." : "Salvar Alterações"}
          </button>

          <button
            onClick={handleDelete}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded"
          >
            Excluir SobeScript
          </button>

          <button
            onClick={() => navigate(-1)}
            className="w-full bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-100 text-slate-900 font-bold py-3 px-6 rounded"
          >
            Voltar
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditSobeScript;
